import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  X,
  RefreshCw,
  Search,
  Users,
  Calendar,
  Check,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ChevronDown,
  Trash2,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import ReusableDropdown, { unifiedControlStyle } from "../../../../../components/ReusableDropdown";
import { getDisplayError } from "../../../../../utils/displayError";

// Same neutral palette + brand accent as ProcessPayrollModal, so the payroll
// modals read as one family.
const buildPalette = (isDark) => ({
  primary: "#2e7cba",
  primarySoft: isDark ? "rgba(46,124,186,0.18)" : "#eef5fb",
  success: isDark ? "#34d399" : "#059669",
  successSoft: isDark ? "rgba(52,211,153,0.12)" : "#ecfdf5",
  warning: isDark ? "#fbbf24" : "#b45309",
  warningSoft: isDark ? "rgba(251,191,36,0.10)" : "#fffbeb",
  danger: isDark ? "#f87171" : "#dc2626",
  dangerSoft: isDark ? "rgba(248,113,113,0.10)" : "#fef2f2",
  violet: isDark ? "#a78bfa" : "#7c3aed",
  violetSoft: isDark ? "rgba(167,139,250,0.12)" : "#f5f3ff",
  textPrimary: isDark ? "#f1f5f9" : "#0f172a",
  textSecondary: isDark ? "#cbd5e1" : "#475569",
  textMuted: isDark ? "#94a3b8" : "#64748b",
  border: isDark ? "#475569" : "#cbd5e1",
  borderLight: isDark ? "#334155" : "#e2e8f0",
  surface: isDark ? "#1e293b" : "#ffffff",
  surfaceSubtle: isDark ? "#162033" : "#f8fafc",
  inputBackground: isDark ? "#0f172a" : "#ffffff",
});

const RecreatePayrollModal = ({
  isOpen,
  onClose,
  selectedMonth,
  currentTheme,
  theme,
  language,
  isRTL,
  t,
  showAlert,
  apiFetch,
  API_BASE_URL,
  clientName,
  initialSelectedInfinityCodes = [],
  onRecreateComplete,
}) => {
  // ── Year and Month selection ──
  const now = new Date();
  const [targetYear, setTargetYear] = useState(
    Number(selectedMonth?.year) || now.getFullYear()
  );
  const [targetMonth, setTargetMonth] = useState(
    Number(selectedMonth?.month) || now.getMonth() + 1
  );

  // ── Employee state ──
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // ── Search & Filter state ──
  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  // Grouping is fixed to department (same as ProcessPayrollModal's employee step).
  const [viewMode] = useState("department"); // 'department' | 'company' | 'list'
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // ── Selection state ──
  const [selectedCodes, setSelectedCodes] = useState(
    () => new Set(initialSelectedInfinityCodes.map(String))
  );
  const [forceRecreate, setForceRecreate] = useState(false);
  // "keep_all" = keep imported & manual rows
  // "clear_imported" = wipe imported rows, keep manual additions safe
  // "clear_all" = wipe both imported and manual additions
  const [clearMode, setClearMode] = useState("keep_all");

  // ── Execution state ──
  const [isRecreating, setIsRecreating] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const isDark = theme === "dark";

  // ── Fetch employees in caller's visibility ──
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoadingEmployees(true);
    setFetchError(null);

    const fetchVisibilityEmployees = async () => {
      try {
        const queryParams = new URLSearchParams({
          viewType: "admin",
          fields: "basic",
        });
        if (clientName) {
          queryParams.set("clientName", clientName);
        }

        const res = await apiFetch(
          `${API_BASE_URL}/employees/visibility?${queryParams.toString()}`
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: Failed to fetch visible employees`);
        }

        const json = await res.json();
        const rawList = Array.isArray(json.employees)
          ? json.employees
          : Array.isArray(json.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];

        if (cancelled) return;

        // Normalize employee data
        const normalized = rawList
          .map((emp) => {
            const infinityCode = String(
              emp.infinityCode || emp["Infinity Code"] || ""
            ).trim();
            if (!infinityCode) return null;

            const name =
              emp.employeeName ||
              emp.name ||
              [emp.fName, emp.lName].filter(Boolean).join(" ") ||
              emp.sr ||
              infinityCode;

            const company =
              emp.workingCompany ||
              emp["Working Company"] ||
              emp.companyName ||
              emp.clientName ||
              "";

            const dept =
              (typeof emp.department === "object"
                ? emp.department?.name
                : emp.department) ||
              emp["Department"] ||
              emp.employeeType ||
              emp.position ||
              "";

            const state = String(emp.employeeState || "Active").trim();

            return {
              infinityCode,
              name,
              company,
              department: dept,
              state,
              raw: emp,
            };
          })
          .filter(Boolean);

        setEmployees(normalized);

        // If initial codes were supplied and exist in the fetched list, select them
        if (initialSelectedInfinityCodes.length > 0) {
          const availableSet = new Set(normalized.map((e) => e.infinityCode));
          const validInitial = initialSelectedInfinityCodes.filter((c) =>
            availableSet.has(String(c))
          );
          if (validInitial.length > 0) {
            setSelectedCodes(new Set(validInitial.map(String)));
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("❌ Error fetching employees for recreate modal:", err);
          setFetchError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoadingEmployees(false);
        }
      }
    };

    fetchVisibilityEmployees();

    return () => {
      cancelled = true;
    };
  }, [isOpen, API_BASE_URL, clientName, apiFetch]);

  // ── Distinct Companies and Departments ──
  const companiesList = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      if (e.company) set.add(e.company);
    });
    return Array.from(set).sort();
  }, [employees]);

  const departmentsList = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      if (e.department) set.add(e.department);
    });
    return Array.from(set).sort();
  }, [employees]);

  // ── Filtered employees ──
  const filteredEmployees = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return employees.filter((emp) => {
      if (companyFilter !== "all" && emp.company !== companyFilter) {
        return false;
      }
      if (departmentFilter !== "all" && emp.department !== departmentFilter) {
        return false;
      }
      if (query) {
        const matchesName = emp.name.toLowerCase().includes(query);
        const matchesCode = emp.infinityCode.toLowerCase().includes(query);
        const matchesDept = emp.department.toLowerCase().includes(query);
        const matchesComp = emp.company.toLowerCase().includes(query);
        if (!matchesName && !matchesCode && !matchesDept && !matchesComp) {
          return false;
        }
      }
      return true;
    });
  }, [employees, searchTerm, companyFilter, departmentFilter]);

  // ── Grouped employees for grouped views ──
  const groupedData = useMemo(() => {
    if (viewMode === "list") return null;

    const groupKey = viewMode === "company" ? "company" : "department";
    const fallbackLabel =
      viewMode === "company"
        ? isRTL
          ? "بدون شركة"
          : "Unspecified Company"
        : isRTL
        ? "بدون قسم"
        : "Unspecified Department";

    const map = new Map();
    filteredEmployees.forEach((emp) => {
      const key = emp[groupKey] || fallbackLabel;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(emp);
    });

    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredEmployees, viewMode, isRTL]);

  // ── Selection handlers ──
  const handleToggleCode = useCallback((code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);

  const handleSelectAllFiltered = useCallback(() => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      filteredEmployees.forEach((emp) => next.add(emp.infinityCode));
      return next;
    });
  }, [filteredEmployees]);

  const handleDeselectAllFiltered = useCallback(() => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      filteredEmployees.forEach((emp) => next.delete(emp.infinityCode));
      return next;
    });
  }, [filteredEmployees]);

  const handleToggleGroup = useCallback((groupEmployees) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      const allGroupSelected = groupEmployees.every((emp) =>
        next.has(emp.infinityCode)
      );

      if (allGroupSelected) {
        groupEmployees.forEach((emp) => next.delete(emp.infinityCode));
      } else {
        groupEmployees.forEach((emp) => next.add(emp.infinityCode));
      }
      return next;
    });
  }, []);

  const toggleCollapseGroup = useCallback((groupName) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  // ── Execute Recreate ──
  const handleRecreate = async () => {
    const codes = Array.from(selectedCodes);
    if (codes.length === 0) {
      if (showAlert) {
        showAlert(
          t("error"),
          t("noEmployeesSelected") || "Please select at least one employee",
          "error"
        );
      }
      return;
    }

    const confirmMsg = (
      t("recreateConfirm") ||
      "Are you sure you want to recreate payroll for {count} employee(s)?"
    ).replace("{count}", codes.length);

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsRecreating(true);
    setStatusMessage(
      `${t("recreatingPayroll") || "Recreating payroll..."} (${codes.length})`
    );

    try {
      const send = (confirmImported) =>
        apiFetch(`${API_BASE_URL}/payrollX/recreate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            infinityCodes: codes,
            year: Number(targetYear),
            month: Number(targetMonth),
            force: forceRecreate,
            clearMode,
            clearImported: clearMode !== "keep_all",
            clearManual: clearMode === "clear_all",
            ...(confirmImported ? { confirmImported: true } : {}),
          }),
        });

      let response = await send(false);
      let resData = await response.json();

      // Imported payrolls are locked: the server recreates them only after
      // the user explicitly agrees to discard their imported values.
      if (response.status === 409 && resData?.code === "IMPORTED_PAYROLLS_REQUIRE_CONFIRMATION") {
        const list = (resData.details?.importedPayrolls || [])
          .slice(0, 10)
          .map((p) => `• ${p.employeeName || p.infinityCode}${p.paymentStatus === "paid" ? (isRTL ? " (مدفوع)" : " (paid)") : ""}`)
          .join("\n");
        const more = (resData.details?.importedPayrolls || []).length > 10 ? "\n…" : "";
        const question = isRTL
          ? "\n\nهل تريد إعادة إنشائها وحذف القيم المستوردة؟"
          : "\n\nRecreate them and discard their imported values?";
        if (!window.confirm(`${resData.error || resData.message}\n\n${list}${more}${question}`)) {
          return;
        }
        response = await send(true);
        resData = await response.json();
      }

      if (!response.ok || resData.success === false) {
        throw new Error(resData.error || resData.message || t("recreateFailed"));
      }

      const summary = resData.data || {};
      let alertMsg =
        resData.message ||
        `${t("recreateSuccess") || "Payroll recreated successfully"}: ${
          summary.recreated || codes.length
        }`;

      if (summary.skippedCount > 0) {
        alertMsg += isRTL ? ` (تم تخطي ${summary.skippedCount})` : ` (${summary.skippedCount} skipped)`;
      }

      if (showAlert) {
        showAlert(
          t("success"),
          alertMsg,
          summary.errorCount > 0 ? "warning" : "success"
        );
      }

      if (onRecreateComplete) {
        onRecreateComplete();
      }
      onClose();
    } catch (err) {
      console.error("❌ recreate payroll failed:", err);
      if (showAlert) {
        showAlert(t("error"), err.message || t("recreateFailed"), "error");
      }
    } finally {
      setIsRecreating(false);
      setStatusMessage("");
    }
  };

  // ── Wizard step (1: period, 2: employees, 3: review) ──
  const [step, setStep] = useState(1);
  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  if (!isOpen) return null;

  const c = buildPalette(isDark);
  const L = (ar, en) => (isRTL ? ar : en);

  const monthNames = [
    { value: 1, label: t("january") || L("يناير", "January") },
    { value: 2, label: t("february") || L("فبراير", "February") },
    { value: 3, label: t("march") || L("مارس", "March") },
    { value: 4, label: t("april") || L("أبريل", "April") },
    { value: 5, label: t("may") || L("مايو", "May") },
    { value: 6, label: t("june") || L("يونيو", "June") },
    { value: 7, label: t("july") || L("يوليو", "July") },
    { value: 8, label: t("august") || L("أغسطس", "August") },
    { value: 9, label: t("september") || L("سبتمبر", "September") },
    { value: 10, label: t("october") || L("أكتوبر", "October") },
    { value: 11, label: t("november") || L("نوفمبر", "November") },
    { value: 12, label: t("december") || L("ديسمبر", "December") },
  ];
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => ({
    value: y,
    label: String(y),
  }));
  const companyOptions = [
    { value: "all", label: L("كل الشركات", "All companies") },
    ...companiesList.map((name) => ({ value: name, label: name })),
  ];
  const departmentOptions = [
    { value: "all", label: L("كل الأقسام", "All departments") },
    ...departmentsList.map((name) => ({ value: name, label: name })),
  ];
  const labelOf = (options, value) => options.find((o) => o.value === value)?.label ?? "";
  const monthLabel = labelOf(monthNames, targetMonth);

  const isFiltering = searchTerm.trim() !== "" || companyFilter !== "all" || departmentFilter !== "all";
  const allFilteredSelected =
    filteredEmployees.length > 0 &&
    filteredEmployees.every((emp) => selectedCodes.has(emp.infinityCode));
  const someFilteredSelected =
    !allFilteredSelected && filteredEmployees.some((emp) => selectedCodes.has(emp.infinityCode));
  const clearFilters = () => {
    setSearchTerm("");
    setCompanyFilter("all");
    setDepartmentFilter("all");
  };

  const clearModes = [
    {
      id: "keep_all",
      tone: c.success,
      soft: c.successSoft,
      icon: ArchiveRestore,
      title: L("الاحتفاظ بالكل", "Keep all"),
      desc: L(
        "يحتفظ بجميع البدلات والخصومات المستوردة والمُضافة يدوياً.",
        "Keeps both imported and manually added adjustments.",
      ),
    },
    {
      id: "clear_imported",
      tone: c.warning,
      soft: c.warningSoft,
      icon: Trash2,
      title: L("حذف المستوردة فقط", "Wipe imported only"),
      desc: L(
        "يحذف المستوردة فقط ويحافظ على المدخلات اليدوية.",
        "Removes imported rows, keeps manual additions.",
      ),
    },
    {
      id: "clear_manual",
      tone: c.violet,
      soft: c.violetSoft,
      icon: Trash2,
      title: L("حذف اليدوية فقط", "Wipe manual only"),
      desc: L(
        "يحذف المُضافة يدوياً فقط ويحافظ على المستوردة.",
        "Removes manual additions, keeps imported rows.",
      ),
    },
    {
      id: "clear_all",
      tone: c.danger,
      soft: c.dangerSoft,
      icon: Trash2,
      title: L("حذف الكل", "Wipe all"),
      desc: L(
        "يحذف كل البدلات والخصومات المستوردة واليدوية (إعادة ضبط كاملة).",
        "Removes all imported and manual adjustments (full reset).",
      ),
    },
  ];
  const activeClearMode = clearModes.find((m) => m.id === clearMode) || clearModes[0];

  const steps = [
    { number: 1, title: L("اختيار الفترة", "Select Period") },
    { number: 2, title: L("اختيار الموظفين", "Choose Employees") },
    { number: 3, title: L("المراجعة والتنفيذ", "Review & Recreate") },
  ];
  const canProceed = step === 1 ? Boolean(targetMonth && targetYear) : selectedCodes.size > 0;

  // ── Shared styles (mirrors ProcessPayrollModal) ──
  const formSection = {
    background: c.surface,
    borderRadius: "14px",
    padding: "28px",
    border: `1px solid ${c.borderLight}`,
  };
  const sectionTitle = {
    color: c.textPrimary,
    fontSize: "18px",
    fontWeight: 700,
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };
  const sectionSubtitle = { color: c.textMuted, fontSize: "14px", margin: "6px 0 0", lineHeight: 1.5 };
  const label = { color: c.textSecondary, fontSize: "13px", fontWeight: 600 };
  const formGroup = { display: "flex", flexDirection: "column", gap: "8px" };
  const controlStyle = {
    ...unifiedControlStyle(theme, isRTL),
    padding: "13px 16px",
    borderRadius: "10px",
    border: `1px solid ${c.border}`,
    background: c.inputBackground,
    color: c.textPrimary,
    fontSize: "14.5px",
  };
  const toolbarControl = {
    height: "46px",
    boxSizing: "border-box",
    borderRadius: "10px",
    border: `1px solid ${c.border}`,
    background: c.inputBackground,
    color: c.textPrimary,
    fontSize: "14.5px",
    outline: "none",
  };
  const checkboxStyle = { width: "18px", height: "18px", margin: 0, cursor: "pointer", flexShrink: 0 };

  const button = (variant, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: "46px",
    padding: "0 22px",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background 0.15s, border-color 0.15s",
    whiteSpace: "nowrap",
    ...(variant === "primary" && { background: c.primary, color: "#ffffff", border: `1px solid ${c.primary}` }),
    ...(variant === "secondary" && { background: c.surface, color: c.textPrimary, border: `1px solid ${c.border}` }),
  });

  const initials = (name) =>
    String(name || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

  const stateBadge = (state) => {
    if (!state || state === "Active") return null;
    const tone = state === "Suspended" ? { color: c.warning, bg: c.warningSoft } : { color: c.danger, bg: c.dangerSoft };
    return (
      <span
        style={{
          padding: "3px 10px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 600,
          color: tone.color,
          background: tone.bg,
          whiteSpace: "nowrap",
        }}
      >
        {state}
      </span>
    );
  };

  // ── Step 1: period ──
  const renderStepPeriod = () => (
    <div className="rp-section" style={formSection}>
      <div style={{ marginBottom: "24px" }}>
        <h3 style={sectionTitle}>
          <Calendar size={20} style={{ color: c.primary }} />
          {L("فترة المرتب المستهدفة", "Target payroll period")}
        </h3>
        <p style={sectionSubtitle}>
          {L(
            "اختر الشهر والسنة اللي هيتعاد إنشاء مرتبها.",
            "Choose the month and year whose payroll will be recreated.",
          )}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
        <div style={formGroup}>
          <label style={label}>{L("الشهر", "Month")}</label>
          <ReusableDropdown
            items={monthNames}
            value={monthLabel}
            onSelect={(item) => setTargetMonth(item.value)}
            getItemKey={(item) => item.value}
            getItemLabel={(item) => item.label}
            searchable={false}
            disabled={isRecreating}
            theme={theme}
            language={language}
            inputStyle={controlStyle}
            maxItems={12}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>{L("السنة", "Year")}</label>
          <ReusableDropdown
            items={yearOptions}
            value={String(targetYear)}
            onSelect={(item) => setTargetYear(item.value)}
            getItemKey={(item) => item.value}
            getItemLabel={(item) => item.label}
            searchable={false}
            disabled={isRecreating}
            theme={theme}
            language={language}
            inputStyle={controlStyle}
          />
        </div>
      </div>

      <button
        type="button"
        className="rp-chip"
        aria-pressed={forceRecreate}
        disabled={isRecreating}
        onClick={() => setForceRecreate((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          width: "100%",
          marginTop: "24px",
          padding: "16px",
          borderRadius: "12px",
          border: `1px solid ${forceRecreate ? c.warning : c.border}`,
          background: forceRecreate ? c.warningSoft : c.surface,
          color: c.textPrimary,
          cursor: isRecreating ? "not-allowed" : "pointer",
          textAlign: "start",
          fontFamily: "inherit",
          transition: "all 0.15s ease",
        }}
      >
        <span
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: forceRecreate ? c.warning : c.surfaceSubtle,
            color: forceRecreate ? "#ffffff" : c.textMuted,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={18} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: "14.5px", fontWeight: 600 }}>
            {t("forceRecreatePaid") || L("إعادة الإنشاء حتى لو المرتب مدفوع", "Force recreate if already paid")}
          </span>
          <span style={{ display: "block", fontSize: "13px", color: c.textMuted, marginTop: "2px" }}>
            {L(
              "من غيره، المرتبات المدفوعة مش بيتعاد إنشاؤها.",
              "Without it, payrolls already marked as paid are skipped.",
            )}
          </span>
        </span>
        <span
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "6px",
            border: `2px solid ${forceRecreate ? c.warning : c.border}`,
            background: forceRecreate ? c.warning : "transparent",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {forceRecreate && <Check size={13} strokeWidth={3} />}
        </span>
      </button>
    </div>
  );

  // ── Step 2: employees ──
  const renderEmployeeRow = (emp) => {
    const isSelected = selectedCodes.has(emp.infinityCode);
    return (
      <div
        key={emp.infinityCode}
        className="rp-row"
        data-selected={isSelected}
        onClick={() => handleToggleCode(emp.infinityCode)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          cursor: "pointer",
          padding: "12px 20px",
          borderTop: `1px solid ${c.borderLight}`,
          borderInlineStart: `3px solid ${isSelected ? c.primary : "transparent"}`,
          background: isSelected ? c.primarySoft : c.surface,
          transition: "background 0.15s",
        }}
      >
        <input type="checkbox" checked={isSelected} onChange={() => {}} style={checkboxStyle} />
        <div
          aria-hidden="true"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: isSelected ? c.primary : c.surfaceSubtle,
            color: isSelected ? "#ffffff" : c.textSecondary,
            border: `1px solid ${isSelected ? c.primary : c.borderLight}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials(emp.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              color: c.textPrimary,
              fontSize: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {emp.name}
          </div>
          <div style={{ fontSize: "12.5px", color: c.textMuted, marginTop: "2px" }}>#{emp.infinityCode}</div>
        </div>
        {stateBadge(emp.state)}
        {emp.company && (
          <div
            className="rp-row-company"
            style={{
              textAlign: "end",
              flexShrink: 0,
              maxWidth: "220px",
              fontSize: "13.5px",
              color: c.textSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {emp.company}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = ([groupName, groupEmployees]) => {
    const collapsed = !isFiltering && collapsedGroups.has(groupName);
    const selectedInGroup = groupEmployees.filter((emp) => selectedCodes.has(emp.infinityCode)).length;
    const fully = selectedInGroup === groupEmployees.length;
    const partially = selectedInGroup > 0 && !fully;
    return (
      <div key={groupName}>
        <div
          className="rp-group-head"
          onClick={() => handleToggleGroup(groupEmployees)}
          style={{
            padding: "13px 20px",
            background: c.surfaceSubtle,
            borderTop: `1px solid ${c.borderLight}`,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            position: "sticky",
            top: 0,
            zIndex: 2,
            transition: "background 0.15s",
          }}
        >
          <input
            type="checkbox"
            checked={fully}
            onChange={() => {}}
            ref={(el) => {
              if (el) el.indeterminate = partially;
            }}
            style={checkboxStyle}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: c.textPrimary }}>{groupName}</div>
            <div style={{ fontSize: "12.5px", color: c.textMuted, marginTop: "2px" }}>
              {L(`${groupEmployees.length} موظف`, `${groupEmployees.length} employee${groupEmployees.length === 1 ? "" : "s"}`)}
              {selectedInGroup > 0 && (
                <span style={{ color: c.primary, fontWeight: 600 }}>
                  {" · "}
                  {L(`${selectedInGroup} محدد`, `${selectedInGroup} selected`)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label={collapsed ? L("توسيع", "Expand") : L("طي", "Collapse")}
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapseGroup(groupName);
            }}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              border: "none",
              background: "transparent",
              color: c.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <ChevronDown
              size={18}
              style={{
                transform: collapsed ? `rotate(${isRTL ? 90 : -90}deg)` : "none",
                transition: "transform 0.2s",
              }}
            />
          </button>
        </div>
        {!collapsed && groupEmployees.map(renderEmployeeRow)}
      </div>
    );
  };

  const emptyState = (content) => (
    <div style={{ padding: "48px 24px", textAlign: "center", color: c.textMuted, fontSize: "14.5px" }}>{content}</div>
  );

  const renderStepEmployees = () => (
    <div className="rp-section" style={formSection}>
      <div style={{ marginBottom: "22px" }}>
        <h3 style={sectionTitle}>
          <Users size={20} style={{ color: c.primary }} />
          {L("اختيار الموظفين", "Choose employees")}
        </h3>
        <p style={sectionSubtitle}>
          {L(
            "حدد الموظفين اللي هيتعاد إنشاء مرتبهم — تقدر تحدد القسم كله مرة واحدة أو موظف موظف.",
            "Pick whose payroll is recreated — select a whole department at once or employees one by one.",
          )}
        </p>
      </div>

      {/* Toolbar */}
      <div className="rp-toolbar" style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "14px" }}>
        <div style={{ position: "relative", flex: "1 1 280px", minWidth: 0 }}>
          <Search
            size={18}
            style={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              insetInlineStart: "14px",
              color: c.textMuted,
              pointerEvents: "none",
            }}
          />
          <input
            type="search"
            className="rp-field"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={L("ابحث بالاسم أو الكود أو القسم...", "Search by name, code or department...")}
            aria-label={L("بحث عن موظف", "Search employees")}
            style={{ ...toolbarControl, width: "100%", padding: "0 14px", paddingInlineStart: "42px" }}
          />
        </div>
        {companiesList.length > 1 && (
          <div style={{ flex: "0 1 220px", minWidth: "170px" }}>
            <ReusableDropdown
              items={companyOptions}
              value={labelOf(companyOptions, companyFilter)}
              onSelect={(item) => setCompanyFilter(item.value)}
              getItemKey={(item) => item.value}
              getItemLabel={(item) => item.label}
              searchable={companyOptions.length > 8}
              theme={theme}
              language={language}
              inputStyle={{ minHeight: "46px", borderColor: companyFilter !== "all" ? c.primary : c.border, background: c.inputBackground }}
            />
          </div>
        )}
        <div style={{ flex: "0 1 220px", minWidth: "170px" }}>
          <ReusableDropdown
            items={departmentOptions}
            value={labelOf(departmentOptions, departmentFilter)}
            onSelect={(item) => setDepartmentFilter(item.value)}
            getItemKey={(item) => item.value}
            getItemLabel={(item) => item.label}
            searchable={departmentOptions.length > 8}
            theme={theme}
            language={language}
            inputStyle={{ minHeight: "46px", borderColor: departmentFilter !== "all" ? c.primary : c.border, background: c.inputBackground }}
          />
        </div>
        {isFiltering && (
          <button
            type="button"
            className="rp-btn rp-btn-secondary"
            onClick={clearFilters}
            style={{
              ...toolbarControl,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "0 14px",
              background: c.surface,
              color: c.textSecondary,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <X size={16} />
            {L("مسح", "Clear")}
          </button>
        )}
        <label
          style={{
            ...toolbarControl,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "0 16px",
            background: allFilteredSelected ? c.primarySoft : c.surface,
            borderColor: allFilteredSelected ? c.primary : c.border,
            opacity: filteredEmployees.length === 0 ? 0.5 : 1,
            marginInlineStart: "auto",
            cursor: filteredEmployees.length === 0 ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          <input
            type="checkbox"
            checked={allFilteredSelected}
            ref={(el) => {
              if (el) el.indeterminate = someFilteredSelected;
            }}
            onChange={allFilteredSelected ? handleDeselectAllFiltered : handleSelectAllFiltered}
            disabled={filteredEmployees.length === 0}
            style={checkboxStyle}
          />
          {isFiltering ? L("تحديد النتائج", "Select results") : L("تحديد الكل", "Select all")}
          <span
            style={{
              background: c.primary,
              color: "#fff",
              borderRadius: "999px",
              padding: "1px 9px",
              fontSize: "12px",
              fontWeight: 700,
            }}
          >
            {filteredEmployees.length}
          </span>
        </label>
      </div>

      {isFiltering && (
        <div style={{ fontSize: "13px", color: c.textMuted, marginBottom: "12px" }}>
          {L(
            `${filteredEmployees.length} من ${employees.length} موظف`,
            `${filteredEmployees.length} of ${employees.length} employees`,
          )}
        </div>
      )}

      {/* List */}
      <div style={{ border: `1px solid ${c.borderLight}`, borderRadius: "12px", overflow: "hidden", background: c.surface }}>
        <div className="rp-scroll" style={{ maxHeight: "calc(100vh - 470px)", minHeight: "260px", overflowY: "auto", marginTop: "-1px" }}>
          {loadingEmployees ? (
            emptyState(
              <span style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
                <Loader2 size={18} className="animate-spin" style={{ color: c.primary }} />
                {L("جاري تحميل الموظفين…", "Loading employees…")}
              </span>,
            )
          ) : fetchError ? (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                margin: "16px",
                padding: "12px 16px",
                borderRadius: "10px",
                background: c.dangerSoft,
                color: c.danger,
                fontSize: "13.5px",
              }}
            >
              <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: "1px" }} />
              <span>{getDisplayError(fetchError, language, { kind: "load" })}</span>
            </div>
          ) : employees.length === 0 ? (
            emptyState(L("لا يوجد موظفين في نطاق رؤيتك.", "No employees in your visibility."))
          ) : filteredEmployees.length === 0 ? (
            emptyState(
              <>
                {L("لا يوجد موظفين مطابقين للبحث.", "No employees match your search.")}{" "}
                <button
                  type="button"
                  onClick={clearFilters}
                  style={{ background: "none", border: "none", color: c.primary, fontWeight: 700, cursor: "pointer", fontSize: "14.5px" }}
                >
                  {L("مسح البحث", "Clear search")}
                </button>
              </>,
            )
          ) : (
            (groupedData || []).map(renderGroup)
          )}
        </div>
      </div>
    </div>
  );

  // ── Step 3: review ──
  const summaryItem = (title, value, tone) => (
    <div
      style={{
        flex: "1 1 200px",
        padding: "16px 18px",
        borderRadius: "12px",
        border: `1px solid ${c.borderLight}`,
        background: c.surfaceSubtle,
      }}
    >
      <div style={{ fontSize: "12.5px", fontWeight: 600, color: c.textMuted }}>{title}</div>
      <div style={{ fontSize: "17px", fontWeight: 700, color: tone || c.textPrimary, marginTop: "4px" }}>{value}</div>
    </div>
  );

  const renderStepReview = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div className="rp-section" style={formSection}>
        <div style={{ marginBottom: "20px" }}>
          <h3 style={sectionTitle}>
            <CheckCircle size={20} style={{ color: c.primary }} />
            {L("ملخص إعادة الإنشاء", "Recreation summary")}
          </h3>
          <p style={sectionSubtitle}>
            {L("راجع البيانات قبل التنفيذ.", "Review the details before running it.")}
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {summaryItem(L("الفترة", "Period"), `${monthLabel} ${targetYear}`)}
          {summaryItem(L("الموظفين", "Employees"), L(`${selectedCodes.size} موظف`, `${selectedCodes.size} employee${selectedCodes.size === 1 ? "" : "s"}`))}
          {summaryItem(
            L("المرتبات المدفوعة", "Paid payrolls"),
            forceRecreate ? L("هيتعاد إنشاؤها", "Will be recreated") : L("هيتم تخطيها", "Will be skipped"),
            forceRecreate ? c.warning : undefined,
          )}
        </div>
      </div>

      <div className="rp-section" style={formSection}>
        <div style={{ marginBottom: "20px" }}>
          <h3 style={sectionTitle}>
            <ArchiveRestore size={20} style={{ color: c.primary }} />
            {L("البدلات والخصومات السابقة", "Previous adjustments")}
          </h3>
          <p style={sectionSubtitle}>
            {L(
              "حدد هيحصل إيه في البدلات والخصومات الموجودة على المرتب الحالي.",
              "Choose what happens to the allowances and deductions already on the current payroll.",
            )}
          </p>
        </div>
        <div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {clearModes.map((mode) => {
            const active = clearMode === mode.id;
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                type="button"
                role="radio"
                aria-checked={active}
                className="rp-chip"
                disabled={isRecreating}
                onClick={() => setClearMode(mode.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: `1px solid ${active ? mode.tone : c.border}`,
                  background: active ? mode.soft : c.surface,
                  color: c.textPrimary,
                  textAlign: "start",
                  cursor: isRecreating ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.15s ease",
                }}
              >
                <span
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: active ? mode.tone : c.surfaceSubtle,
                    color: active ? "#ffffff" : mode.tone,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={18} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "14.5px", fontWeight: 600 }}>{mode.title}</span>
                  <span style={{ display: "block", fontSize: "13px", color: c.textMuted, marginTop: "2px" }}>{mode.desc}</span>
                </span>
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: `2px solid ${active ? mode.tone : c.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {active && <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: mode.tone }} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;
  const NextIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div
      className="rp-overlay payroll-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
        overflow: "hidden",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isRecreating) onClose();
      }}
    >
      <style>{`
        .rp-btn:not(:disabled):hover { filter: brightness(0.94); }
        .rp-btn-secondary:not(:disabled):hover { background: ${c.surfaceSubtle} !important; }
        .rp-close:not(:disabled):hover { background: ${c.surfaceSubtle} !important; color: ${c.textPrimary} !important; }
        .rp-field:focus { border-color: ${c.primary} !important; }
        .rp-row:hover { background: ${c.surfaceSubtle} !important; }
        .rp-row[data-selected="true"]:hover { background: ${c.primarySoft} !important; filter: brightness(0.98); }
        .rp-group-head:hover { background: ${isDark ? "#1b2638" : "#f1f5f9"} !important; }
        .rp-chip:not(:disabled):hover { border-color: ${c.primary} !important; }
        .rp-modal input[type="checkbox"] { accent-color: ${c.primary}; }
        @media (max-width: 640px) {
          .rp-overlay { padding: 8px !important; }
          .rp-pad { padding-left: 16px !important; padding-right: 16px !important; }
          .rp-section { padding: 18px !important; }
          .rp-toolbar > * { flex: 1 1 100% !important; margin-inline-start: 0 !important; }
          .rp-step-label, .rp-row-company { display: none; }
          .rp-footer-summary { display: none !important; }
        }
      `}</style>

      <div
        className="rp-modal"
        dir={isRTL ? "rtl" : "ltr"}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rp-title"
        style={{
          background: c.surface,
          border: `1px solid ${c.borderLight}`,
          colorScheme: isDark ? "dark" : "light",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "1180px",
          height: "calc(100vh - 32px)",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          color: c.textPrimary,
        }}
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div
          className="rp-pad"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            padding: "22px 32px",
            borderBottom: `1px solid ${c.borderLight}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: c.primarySoft,
                color: c.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RefreshCw size={22} className={isRecreating ? "animate-spin" : ""} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 id="rp-title" style={{ color: c.textPrimary, fontSize: "22px", fontWeight: 700, margin: 0, lineHeight: 1.25 }}>
                {t("recreatePayrollTitle") || L("إعادة إنشاء مرتبات الموظفين", "Recreate Employee Payroll")}
              </h1>
              <div style={{ fontSize: "13.5px", color: c.textMuted, marginTop: "2px" }}>
                {L(
                  `الخطوة ${step} من ${steps.length} — ${steps[step - 1].title}`,
                  `Step ${step} of ${steps.length} — ${steps[step - 1].title}`,
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="rp-close"
            onClick={onClose}
            disabled={isRecreating}
            aria-label={L("إغلاق", "Close")}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "transparent",
              border: "none",
              cursor: isRecreating ? "not-allowed" : "pointer",
              color: c.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
          >
            <X size={22} />
          </button>
        </div>

        {/* ── Stepper ──────────────────────────────────────────────────── */}
        <div
          className="rp-pad"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "18px 32px",
            borderBottom: `1px solid ${c.borderLight}`,
            background: c.surface,
          }}
        >
          {steps.map((s, index) => {
            const isDone = step > s.number;
            const isActive = step === s.number;
            return (
              <React.Fragment key={s.number}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: 700,
                      background: isDone || isActive ? c.primary : c.surface,
                      color: isDone || isActive ? "#ffffff" : c.textMuted,
                      border: `2px solid ${isDone || isActive ? c.primary : c.border}`,
                      transition: "all 0.2s",
                    }}
                  >
                    {isDone ? <Check size={16} strokeWidth={3} /> : s.number}
                  </div>
                  <div className="rp-step-label">
                    <div style={{ fontSize: "11.5px", color: c.textMuted, fontWeight: 600 }}>
                      {L(`الخطوة ${s.number}`, `Step ${s.number}`)}
                    </div>
                    <div
                      style={{
                        fontSize: "14.5px",
                        fontWeight: isActive ? 700 : 600,
                        color: isActive || isDone ? c.textPrimary : c.textMuted,
                      }}
                    >
                      {s.title}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: "24px",
                      height: "2px",
                      borderRadius: "2px",
                      background: isDone ? c.primary : c.borderLight,
                      transition: "background 0.2s",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="rp-scroll rp-pad" style={{ padding: "28px 32px", overflowY: "auto", flex: 1, background: c.surfaceSubtle }}>
          {step === 1 && renderStepPeriod()}
          {step === 2 && renderStepEmployees()}
          {step === 3 && renderStepReview()}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div
          className="rp-pad"
          style={{
            padding: "18px 32px",
            borderTop: `1px solid ${c.borderLight}`,
            background: c.surface,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div className="rp-footer-summary" style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
            {isRecreating ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "14px", color: c.textSecondary }}>
                <Loader2 size={16} className="animate-spin" style={{ color: c.primary }} />
                {statusMessage}
              </span>
            ) : step > 1 && selectedCodes.size > 0 ? (
              <>
                <span style={{ fontSize: "12.5px", color: c.textMuted, fontWeight: 600 }}>
                  {L(`${monthLabel} ${targetYear}`, `${monthLabel} ${targetYear}`)}
                </span>
                <span style={{ fontSize: "18px", fontWeight: 700, color: c.textPrimary }}>
                  {L(
                    `${selectedCodes.size} موظف محدد`,
                    `${selectedCodes.size} employee${selectedCodes.size === 1 ? "" : "s"} selected`,
                  )}
                  {step === 3 && (
                    <span style={{ fontSize: "14px", fontWeight: 600, color: activeClearMode.tone }}>
                      {" · "}
                      {activeClearMode.title}
                    </span>
                  )}
                </span>
              </>
            ) : null}
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap", marginInlineStart: "auto" }}>
            {step > 1 ? (
              <button
                type="button"
                className="rp-btn rp-btn-secondary"
                onClick={() => setStep((s) => s - 1)}
                disabled={isRecreating}
                style={button("secondary", isRecreating)}
              >
                <BackIcon size={18} />
                {t("back") || L("رجوع", "Back")}
              </button>
            ) : (
              <button
                type="button"
                className="rp-btn rp-btn-secondary"
                onClick={onClose}
                disabled={isRecreating}
                style={button("secondary", isRecreating)}
              >
                {t("cancel") || L("إلغاء", "Cancel")}
              </button>
            )}

            {step < 3 ? (
              <button
                type="button"
                className="rp-btn"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canProceed || isRecreating}
                style={button("primary", !canProceed || isRecreating)}
              >
                {t("nextStep") || L("التالي", "Next")}
                <NextIcon size={18} />
              </button>
            ) : (
              <button
                type="button"
                className="rp-btn"
                onClick={handleRecreate}
                disabled={isRecreating || selectedCodes.size === 0}
                style={button("primary", isRecreating || selectedCodes.size === 0)}
              >
                {isRecreating ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                {isRecreating
                  ? t("recreatingPayroll") || L("جاري إعادة الإنشاء...", "Recreating...")
                  : L(`إعادة إنشاء (${selectedCodes.size})`, `Recreate payroll (${selectedCodes.size})`)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecreatePayrollModal;
